import useAddChargingConnector from '../../../hooks/charging-connectors/useCreateChargingConnector.ts';
import MutateChargingConnectorForm, {
  MutateChargingConnectorFormValues,
} from '../../ui/places/MutateChargingConnectorForm.tsx';
import { AddChargingConnectorRequest } from '../../../api/requests/charging-connector-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@mantine/core';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import { ErrorResponse } from '../../../api/responses/error-response.ts';

interface AddChargingConnectorModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  evseId: string;
  onSuccess: () => void;
}

export function AddChargingConnectorModal({
  opened,
  onClose,
  serviceId,
  evseId,
  onSuccess,
}: AddChargingConnectorModalProps) {
  const queryClient = useQueryClient();

  const { mutateAsync: addChargingConnector } = useAddChargingConnector();

  const { mutate: handleSubmit, isPending: isAddChargingConnectorPending } =
    useMutation<void, ErrorResponse, MutateChargingConnectorFormValues>({
      mutationFn: async (formValues: MutateChargingConnectorFormValues) => {
        const addChargingConnectorRequest: AddChargingConnectorRequest = {
          evseId: evseId,
          connectorSerialNumber: formValues.connectorSerialNumber,
          connectorType: formValues.connectorType,
          isEVCCIDSupported: formValues.isEVCCIDSupported
            ? formValues.isEVCCIDSupported
            : 'UNKNOWN',
          powerType: formValues.powerType ? formValues.powerType : 'UNKNOWN',
          maxPower: {
            value: formValues.maxPowerValue,
            unit: 'kW',
          },
          status: 'UNKNOWN',
          chargingRate: {
            rateText: formValues.rateText,
            name: null,
            description: null,
            effectivePeriod: null,
          },
        };

        await addChargingConnector(addChargingConnectorRequest);

        await queryClient.invalidateQueries({
          queryKey: ['chargingServices', serviceId, 'evses'],
          exact: true,
        });

        await queryClient.invalidateQueries({
          queryKey: ['evses', evseId],
          exact: true,
        });
      },
      onSuccess: () => {
        notifySuccess('新增充電槍成功');
        onSuccess?.();
      },
      onError: (error) => {
        notifyError('新增充電槍失敗', error.message);
      },
    });

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeOnClickOutside={false}
      title="新增充電資訊 - 充電槍"
      centered
      size={659}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      <MutateChargingConnectorForm
        initialValues={{
          connectorSerialNumber: '',
          connectorType: null,
          maxPowerValue: null,
          powerType: null,
          isEVCCIDSupported: null,
          rateText: '',
        }}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        isSubmitting={isAddChargingConnectorPending}
        submitButtonText={'新增充電槍'}
      />
    </Modal>
  );
}
